import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks';
import { LoadingSpinner } from '@/components';
import LandingPage from '@/pages/HomePage';
import { ErrorPage } from '@/pages/ErrorPage';
import { isDev } from './constants';
import { isMobilePhone } from '@/mobile/detection/deviceDetection';
import { SpaceAccessGuard } from '@/components/SpaceAccessGuard';

// Lazy load mobile components for code splitting
const MobileHome = React.lazy(() =>
  import('@/mobile/MobileHome').then((module) => ({ default: module.MobileHome })),
);
const MobileStudio = React.lazy(() =>
  import('@/mobile/MobileStudio').then((module) => ({ default: module.MobileStudio })),
);

const KNOWN_PATHS = ['/studio', '/devices', '/live', '/settings', '/connect'];

function Routing() {
  const { isAuthenticated, isLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = isMobilePhone();
  // Exact match (after normalizing a trailing slash): `/mobile-foo`,
  // `/mobile/settings`, etc. must fall through to the desktop 404 path
  // rather than silently render MobileHome. `/mobile/` is treated as
  // `/mobile` so bookmarked trailing-slash URLs still resolve.
  const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
  const isMobileRoute = normalizedPath === '/mobile' || normalizedPath === '/mobile/studio';

  // Mobile phone redirect logic
  useEffect(() => {
    if (!isPhone) return;
    if (normalizedPath === '/' || normalizedPath === '/studio') {
      navigate(`/mobile/studio${location.search}${location.hash}`, { replace: true });
    }
  }, [isPhone, normalizedPath, location.search, location.hash, navigate]);

  if (isPhone && (normalizedPath === '/' || normalizedPath === '/studio')) {
    return <LoadingSpinner />;
  }

  // Mobile routes
  if (isMobileRoute) {
    if (isLoading || !isAuthenticated) {
      return <LoadingSpinner />;
    }
    return (
      <SpaceAccessGuard>
        <React.Suspense fallback={<LoadingSpinner />}>
          {normalizedPath === '/mobile/studio' ? <MobileStudio /> : <MobileHome />}
        </React.Suspense>
      </SpaceAccessGuard>
    );
  }

  // Desktop: unknown paths render a 404 below.
  const isKnownPath =
    normalizedPath === '/' ||
    KNOWN_PATHS.some((p) => normalizedPath === p || normalizedPath.startsWith(p + '/')) ||
    (isDev && (normalizedPath === '/demo' || normalizedPath.startsWith('/demo/')));
  if (!isKnownPath) {
    return (
      <ErrorPage error={{ message: 'Page not found', name: '404' }} resetErrorBoundary={() => {}} />
    );
  }

  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }
  return (
    <SpaceAccessGuard>
      <LandingPage />
    </SpaceAccessGuard>
  );
}

export default Routing;
